/**
 * What the policy bar says about each policy: only the tags that apply.
 *
 * "Draft" told you nothing — it only meant "not live and never was". So a
 * policy carries no tag unless something is true of it: customers run on it
 * (live), your own calls run on it (my account), or it was live once and was
 * replaced (was live). One with no tag is one you can still change.
 */
import type { PipelineVersion } from '../types';

export type PolicyTag = 'live' | 'mine' | 'was_live';

export const TAG_WORDS: Record<PolicyTag, string> = {
  live: 'live',
  mine: 'my account',
  was_live: 'was live',
};

/** Your own calls run on the policy you picked, else the live one. */
export function mineId(testingId: number | null, stableId: number | null): number | null {
  return testingId ?? stableId;
}

export function policyTags(v: PipelineVersion, mine: number | null): PolicyTag[] {
  const tags: PolicyTag[] = [];
  if (v.status === 'STABLE') tags.push('live');
  if (v.id === mine) tags.push('mine');
  if (v.status === 'RETIRED') tags.push('was_live');
  return tags;
}

/** The real number, so a renamed policy cannot pass for another one. */
export function policyNumber(v: PipelineVersion): string {
  return v.version != null ? `v${v.version}` : v.name;
}

/** The custom name, when there is one worth showing beside the number. */
export function policyNickname(v: PipelineVersion): string | null {
  const name = v.name.trim();
  return name && name !== policyNumber(v) ? name : null;
}

/** Live first, then the rest newest first. */
export function barOrder(versions: PipelineVersion[]): PipelineVersion[] {
  const rank = (v: PipelineVersion) => (v.status === 'STABLE' ? 0 : 1);
  return [...versions].sort((a, b) => rank(a) - rank(b) || (b.version ?? 0) - (a.version ?? 0) || b.id - a.id);
}

/** One sentence on what this policy is and what you can do with it. */
export function policySentence(v: PipelineVersion): string {
  if (v.status === 'STABLE') return 'Customers run on this. It cannot be changed - copy it to a new policy to change it.';
  if (v.status === 'RETIRED') return 'This was live before. It cannot be changed - make it live again to switch back.';
  return 'Not live yet, so you can change it. Use it on your account to try it, then make it live.';
}
